import { Component, ElementRef, Inject, Output, EventEmitter } from '@angular/core';
import { SETTINGS } from '@lsq-frontend/angular-shared/settings';
import { ContentTypes, ErrorMessageHelper } from '@lsq-frontend/common-ui';
import { AppSettings } from '@lsq-frontend/fasttrack-shared/environment';
import { FileUpload, Uploader } from 'rxjs-uploader';
import { first, map, shareReplay } from 'rxjs/operators';
import { Observable, Subject } from 'rxjs';
import { forLifeOf } from '@lsq-frontend/common-ui';
import { LifecycleAware } from '@lsq-frontend/common-ui';
import { AlertService } from '@lsq-frontend/angular-shared/features/alert-service';
import { I18n } from '@ngx-translate/i18n-polyfill';

export class PurchaseRequestValidationError {
    public invoiceId: number;
    public message: string;
}

@LifecycleAware()
@Component({
    selector: 'new-purchase-requests-modal',
    templateUrl: './new-purchase-requests-modal.component.html',
    styleUrls: ['./new-purchase-requests-modal.component.scss']
})
export class NewPurchaseRequestsModalComponent {
    @Output() private onValidationErrors = new EventEmitter<
        Array<PurchaseRequestValidationError>
    >();
    @Output() private onSuccessfulUpload = new EventEmitter<void>();

    public readonly attachFileI18n = this._i18n('Attach CSV file');

    public hasFileSelected: boolean;
    public uploadErrorMessage: string;
    public fileUpload$: Observable<FileUpload>;
    public uploadButtonClickSubject = new Subject();
    public fileUploader: Uploader = new Uploader<FileUpload>({
        allowedContentTypes: [ContentTypes.Csv],
        requestOptions: {
            url: `${this._settings.httpApiServiceNameUrlMap['fundingApi']}/v1/createPurchaseRequest`,
            withCredentials: true
        },
        fileSizeLimitMb: 5,
        fileCountLimit: 1,
        disallowedContentTypeErrorMessage: () => this.uploadWrongFileTypeErrorMessageI18N,
        disallowedContentSizeErrorMessage: () => this.contentSizeErrorMessageI18N,
        fileUploadedCallback: (fileUpload: FileUpload) => {
            if (fileUpload.responseCode == 200) {
                this.onSuccessfulUpload.emit();
            } else if (fileUpload.responseCode == 400) {
                const errors: PurchaseRequestValidationError[] = fileUpload.responseBody;
                this.onValidationErrors.emit(errors);
            } else {
                this._alertService.error(this._errorMessageHelper.GENERIC_ERROR);
            }
        },
        allFilesQueuedCallback: async (fileUploads: FileUpload[]) => {
            this.uploadErrorMessage = null;
            await this.uploadButtonClickSubject.pipe(first()).toPromise();
            return [new FileUpload(fileUploads[0].file)];
        }
    });

    private readonly genericErrorMessageI18N = this._i18n(
        'The upload was not successful, please try again and if the problem persists contact support at <a href="mailto:fasttrack-support@lsq.com" class="mail-to preserve-case">fasttrack-support@lsq.com</a>'
    );
    private readonly uploadWrongFileTypeErrorMessageI18N = this._i18n(
        'Your file upload must be in CSV format.'
    );
    private readonly contentSizeErrorMessageI18N = this._i18n('Your file exceeds the 5mb limit.');

    constructor(
        @Inject(SETTINGS) private _settings: AppSettings,
        private _alertService: AlertService,
        private _errorMessageHelper: ErrorMessageHelper,
        private _i18n: I18n
    ) {
        this.fileUploader.errorStream.pipe(forLifeOf(this)).subscribe((error) => {
            if (
                error.message === this.uploadWrongFileTypeErrorMessageI18N ||
                error.message === this.contentSizeErrorMessageI18N
            ) {
                this.uploadErrorMessage = error.message;
            } else {
                this.uploadErrorMessage = this.genericErrorMessageI18N;
            }
        });
    }

    handleFileSelected(fileRef: string) {
        this.hasFileSelected = !!fileRef;
    }

    setFileSource(fileSourceElement: ElementRef) {
        this.fileUpload$ = this.fileUploader
            .streamFileUploads(fileSourceElement.nativeElement as HTMLElement)
            .pipe(
                map(([fileUpload]) => fileUpload),
                shareReplay(1)
            );
    }
}
